import { Blocks } from "blockly/core";
import { javascriptGenerator } from "blockly/javascript";
import * as Blockly from "blockly";
import { CharacterConfig, ObjectConfig, TextObjectConfig } from "../../../Phaser/types/config";

/**
 * Définit tous les blocs "fonctions" (timer, delai etc.)
 */
export function defineFonctionsBlocks(
  characters: CharacterConfig[],
  objects: ObjectConfig[],
  texts: TextObjectConfig[]
) {
  const defaultTextId = texts?.[0]?.id ?? "text_id";


  // ==========================
  // 1) on_timer_event
  // ==========================
  Blocks["on_timer"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("à chaque (secondes)")
        .appendField(new Blockly.FieldNumber(1, 0, Infinity, 1), "SECONDS")
        .appendField("faire");

      this.appendStatementInput("DO")
        .setCheck(null)
        .appendField("instructions");

      this.setColour(210);
      this.setTooltip(
        "Déclenche toutes les X secondes. A adapter selon votre besoin."
      );
      this.setHelpUrl("");
    },
  };
  javascriptGenerator.forBlock["on_timer"] = function (block) {
    const seconds = block.getFieldValue("SECONDS") || 1;
    const branch = javascriptGenerator.statementToCode(block, "DO");
    return `
        (window.eventManager).addTimerEvent(${seconds}, async function(eventData){
           ${branch}
        }, true );
        \n`;
  };

  // ==========================
  // 2) delai
  // ==========================
  Blocks["delai"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("Après un délai de")
        .appendField(new Blockly.FieldNumber(1, 0, Infinity, 1), "SECONDS")
        .appendField("secondes, faire");

      this.appendStatementInput("DO")
        .setCheck(null)
        .appendField("instructions");

      this.setColour(210);
      this.setTooltip(
        "Déclenche l'événement après un délai unique, après X secondes. À adapter selon votre besoin."
      );
      this.setHelpUrl("");
    },
  };
  javascriptGenerator.forBlock["delai"] = function (block) {
    const seconds = block.getFieldValue("SECONDS") || 1;
    const branch = javascriptGenerator.statementToCode(block, "DO");
    return `
        (window.eventManager).addTimerEvent(${seconds}, async function(eventData){
           ${branch}
        }, false );
        \n`;
  };

  Blocks["on_text_starts_with"] = {
    init: function () {     

      // Champ pour la sous-chaîne
      const substringField = new Blockly.FieldTextInput("A");

      this.appendDummyInput()
        .appendField("quand le texte")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID")
        .appendField("commence par")
        .appendField(substringField, "SUBSTRING");

      this.appendStatementInput("DO")
        .setCheck(null)
        .appendField("faire");

      this.setColour(210);
      this.setTooltip("Déclenche lorsque le contenu du texte choisi commence par la lettre/sous-chaîne indiquée.");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["on_text_starts_with"] = function (block) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const substring = block.getFieldValue("SUBSTRING") || "";
    const branch = javascriptGenerator.statementToCode(block, "DO");
    const code = `
(window.eventManager).addTextStartsWithListener('${textId}', '${substring}', async function(eventData){
    ${branch}
});
\n`;
    return code;
  };

  Blocks["on_text_ends_with"] = {
    init: function () {

      // Champ pour la sous-chaîne
      const substringField = new Blockly.FieldTextInput("Z");

      this.appendDummyInput()
        .appendField("quand le texte")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID")
        .appendField("se termine par")
        .appendField(substringField, "SUBSTRING");

      this.appendStatementInput("DO")
        .setCheck(null)
        .appendField("faire");

      this.setColour(210);
      this.setTooltip("Déclenche lorsque le contenu du texte choisi se termine par la lettre/sous-chaîne indiquée.");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["on_text_ends_with"] = function (block) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const substring = block.getFieldValue("SUBSTRING") || "";
    const branch = javascriptGenerator.statementToCode(block, "DO");
    const code = `
(window.eventManager).addTextEndsWithListener('${textId}', '${substring}', async function(eventData){
    ${branch}
});
\n`;
    return code;
  };


  // ================ TO UPPERCASE ================
  Blocks["text_to_uppercase"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("convert to UPPERCASE")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(230);
      this.setTooltip("Convertit tout le texte en MAJUSCULES");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["text_to_uppercase"] = function (block: any) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const blockId = block.id;
    return `
      highlightBlock('${blockId}');
      await toUppercaseTextContent('${textId}');
    \n`;
  };

  // ================ TO LOWERCASE ================
  Blocks["text_to_lowercase"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("convert to lowercase")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(230);
      this.setTooltip("Convertit tout le texte en minuscules");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["text_to_lowercase"] = function (block: any) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const blockId = block.id;
    return `
      highlightBlock('${blockId}');
      await toLowercaseTextContent('${textId}');
    \n`;
  };

  // ================ REVERSE TEXT ================
  Blocks["text_reverse"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("reverse text of")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(230);
      this.setTooltip("Inverse l'ordre des caractères du texte");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["text_reverse"] = function (block: any) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const blockId = block.id;
    return `
      highlightBlock('${blockId}');
      await reverseTextContent('${textId}');
    \n`;
  };

  // ================ CUT TEXT ================
  Blocks["text_cut"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("cut text of")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");
      this.appendValueInput("START_INDEX")
        .setCheck("Number")
        .appendField("start index");
      this.appendValueInput("END_INDEX")
        .setCheck("Number")
        .appendField("end index");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(230);
      this.setTooltip("Découpe le texte selon un intervalle [start, end).");
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["text_cut"] = function (block: any) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const startIndex =
      javascriptGenerator.valueToCode(block, "START_INDEX", 0) || "0";
    const endIndex =
      javascriptGenerator.valueToCode(block, "END_INDEX", 0) || "0";
    const blockId = block.id;
    return `
      highlightBlock('${blockId}');
      await cutTextContent('${textId}', ${startIndex}, ${endIndex});
    \n`;
  };

  // ================ FIND WORD POSITION ================
  // ICI, on veut un bloc qui RENVOIE un nombre
  Blocks["text_find_word_position"] = {
    init: function () {
      this.appendValueInput("WORD")
        .setCheck("String")
        .appendField("find position of word in")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");

      // On transforme le bloc en bloc "valeur" qui renvoie un Number
      this.setOutput(true, "Number");

      this.setColour(230);
      this.setTooltip(
        "Recherche un mot dans le texte et retourne sa position (1-based) ou -1 si non trouvé."
      );
      this.setHelpUrl("");
    },
  };


  javascriptGenerator.forBlock["text_find_word_position"] = function (block) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const word =
      javascriptGenerator.valueToCode(block, "WORD", 0) || '""';

    return [
      `(await findTextWordPosition('${textId}', ${word}))`,
      0
    ];
  };



  // ================ REPLACE WORD (ALL OCCURRENCES) ================
  Blocks["text_replace_word"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("replace all occurrences in")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");
      this.appendValueInput("OLD_WORD")
        .setCheck("String")
        .appendField("old word");
      this.appendValueInput("NEW_WORD")
        .setCheck("String")
        .appendField("new word");
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(230);
      this.setTooltip(
        "Remplace toutes les occurrences d'un mot par un autre dans le texte."
      );
      this.setHelpUrl("");
    },
  };

  javascriptGenerator.forBlock["text_replace_word"] = function (block: any) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const oldWord =
      javascriptGenerator.valueToCode(block, "OLD_WORD", 0) || '""';
    const newWord =
      javascriptGenerator.valueToCode(block, "NEW_WORD", 0) || '""';
    const blockId = block.id;
    return `
      highlightBlock('${blockId}');
      await replaceWordInText('${textId}', ${oldWord}, ${newWord});
    \n`;
  };

  // ================ COUNT TEXT LENGTH ================
  Blocks["text_count_length"] = {
    init: function () {
      this.appendDummyInput()
        .appendField("length of")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");

      // Sortie de type Number
      this.setOutput(true, "Number");

      this.setColour(230);
      this.setTooltip("Retourne la longueur (nombre de caractères) du texte.");
      this.setHelpUrl("");
    },
  };


  javascriptGenerator.forBlock["text_count_length"] = function (block) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    return [
      `(await countTextLength('${textId}'))`,
      0 // 0 = aucun besoin de parenthèses
    ];
  };



  // ================ COUNT CHARACTER OCCURRENCES ================
  Blocks["text_count_char_occurrences"] = {
    init: function () {
      this.appendValueInput("CHAR")
        .setCheck("String")
        .appendField("count occurrences of character");
      this.appendDummyInput()
        .appendField("in")
        .appendField(new Blockly.FieldTextInput(defaultTextId), "TEXT_ID");

      // Sortie Number
      this.setOutput(true, "Number");

      this.setColour(230);
      this.setTooltip(
        "Retourne le nombre d'occurrences d'un caractère dans le texte."
      );
      this.setHelpUrl("");
    },
  };


  javascriptGenerator.forBlock["text_count_char_occurrences"] = function (block) {
    const textId = block.getFieldValue("TEXT_ID") || defaultTextId;
    const character =
      javascriptGenerator.valueToCode(block, "CHAR", 0) || '""';
    return [
      `(await countCharacterOccurrences('${textId}', ${character}))`,
      0
    ];
  };

}
